// @ts-check

/**
 * @file Renders the uploaded file edit screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Platform, View} from "react-native"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import {invalidateUploadedFileImageSource} from "../../components/form/form-html-editor/upload-session"
import Text from "@kaspernj/api-maker/build/utils/text"
import {UploadedFile} from "../../frontend-models/uploaded-file"
import {optionalRouteParam} from "../../shared/route-params"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {ChangeEvent, NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} UploadedFilesEditScreenProps */

/**
 * @typedef {object} UploadedFilesEditScreenState
 * @property {boolean} loading Whether the screen is fetching the existing upload.
 * @property {{contentBase64: string, contentType: string, filename: string} | null} selectedUploadedFile Browser-selected replacement file payload.
 * @property {boolean} saving Whether the save action is in progress.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown after save or load failures.
 * @property {UploadedFile | null} uploadedFile Existing uploaded file model or new draft model.
 */

/**
 * Uploaded file edit screen using frontend models.
 * @augments {ShapeComponent<UploadedFilesEditScreenProps, UploadedFilesEditScreenState>}
 */
class UploadedFilesEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string | null} */
  uploadedFileID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  loadRequestId = 0
  /** @type {UploadedFilesEditScreenState} */
  state = {
    loading: false,
    selectedUploadedFile:
      /** @type {{contentBase64: string, contentType: string, filename: string} | null} */ null,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    uploadedFile: /** @type {UploadedFile | null} */ null
  }

  /**
   * Initializes route state and loads the uploaded file when editing.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const initialResourceId = normalizeQueryNumber(
      optionalRouteParam(params, "resourceId", "Resource ID") ??
        optionalRouteParam(params, "resource_id", "Resource ID")
    )
    const initialResourceType = normalizeQueryString(
      optionalRouteParam(params, "resourceType", "Resource type") ??
        optionalRouteParam(params, "resource_type", "Resource type")
    )
    const initialTitle = normalizeQueryString(optionalRouteParam(params, "title", "Title"))

    const uploadedFileID = optionalRouteParam(params, "id", "Uploaded file ID")
    this.params = params
    this.router = router
    this.uploadedFileID = uploadedFileID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      const requestId = ++this.loadRequestId
      if (!uploadedFileID) {
        this.s.loading = false
        this.s.status = null
        this.s.uploadedFile = new UploadedFile({
          resourceId: initialResourceId,
          resourceType: initialResourceType,
          title: initialTitle
        })
        return
      }

      this.s.loading = true
      this.s.status = null
      this.s.uploadedFile = null

      const loadData = async () => {
        try {
          const uploadedFile = await UploadedFile
            .find(uploadedFileID)
          if (requestId !== this.loadRequestId) return
          this.s.loading = false
          this.s.uploadedFile = uploadedFile
        } catch (error) {
          if (requestId !== this.loadRequestId) return
          this.s.loading = false
          this.s.uploadedFile = null
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        if (requestId === this.loadRequestId) {
          this.loadRequestId += 1
        }
      }
    }, [initialResourceId, initialResourceType, initialTitle, uploadedFileID])
  }

  /**
   * Renders the uploaded file form.
   * @returns {ReactNode} Uploaded file edit screen content.
   */
  render() {
    const {loading, saving, uploadedFile} = this.s
    const {selectedUploadedFile, status} = this.s
    const currentFileName = selectedUploadedFile?.filename || uploadedFile?.fileName() || _("No file chosen")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="uploadedFileEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="uploadedFileEditScreen"
      >
        <Text
          style={styles.screenTitle ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="uploadedFileEditScreen/title"
        >
          {this.tt.uploadedFileID ? _("Edit file") : _("New file")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCard ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="uploadedFileEditScreen/form"
          >
            <FormTextInput
              defaultValue={uploadedFile ? uploadedFile.title() || "" : ""}
              label={_("Title")}
              name="title"
              testID="uploadedFileEditScreen/titleInput"
            />
            <View
              style={styles.fileField ||= {
                gap: 8
              }}
              testID="uploadedFileEditScreen/fileField"
            >
              <Text
                style={styles[`fileLabel-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="uploadedFileEditScreen/fileLabel"
              >
                {_("File")}
              </Text>
              {Platform.OS === "web" &&
                <input
                  data-testid="uploadedFileEditScreen/fileInput"
                  onChange={this.tt.onFileInputChange}
                  type="file"
                />
              }
              {Platform.OS !== "web" &&
                <Text
                  style={styles[`fileUploadHint-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted,
                    fontSize: 14
                  }}
                  testID="uploadedFileEditScreen/fileUploadHint"
                >
                  {_("File uploads are only supported on the web")}
                </Text>
              }
              {Platform.OS === "web" &&
                <Text
                  style={styles[`fileName-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.text,
                    fontSize: 14
                  }}
                  testID="uploadedFileEditScreen/fileName"
                >
                  {currentFileName}
                </Text>
              }
            </View>
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="uploadedFileEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="uploadedFileEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves a new or existing uploaded file.
   * @returns {Promise<void>} Resolves after save handling completes.
   */
  onSubmit = async () => {
    const {params, router, uploadedFileID} = this.tt
    const {selectedUploadedFile, uploadedFile} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      if (uploadedFileID && uploadedFile) {
        if (selectedUploadedFile) {
          await uploadedFile.replaceFile({
            title: values.title || "",
            uploadedFile: selectedUploadedFile
          })
          invalidateUploadedFileImageSource(uploadedFileID)
        } else {
          await uploadedFile.update({
            title: values.title || ""
          })
        }

        router.push(
          buildRoutePath(params, `/uploaded-files/${uploadedFileID}`)
        )
      } else {
        if (!selectedUploadedFile) {
          throw new Error(_("A file upload is required"))
        }

        const response = await UploadedFile.createWithFile({
          resourceId: uploadedFile?.resourceId(),
          resourceType: uploadedFile?.resourceType(),
          title: values.title || "",
          uploadedFile: selectedUploadedFile
        })
        const createdId = String(response.id || "")

        if (!createdId) {
          throw new Error(_("Could not save the file"))
        }

        router.push(buildRoutePath(params, `/uploaded-files/${createdId}`))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Stores the selected browser file as a base64 upload payload.
   * @param {ChangeEvent<HTMLInputElement>} event Change object from the web file input.
   * @returns {void} Nothing.
   */
  onFileInputChange = (event) => {
    const file = event.target.files?.[0]

    if (!file) {
      this.s.selectedUploadedFile = null
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : ""
      const contentBase64 = dataUrl.includes(",")
        ? dataUrl.split(",", 2)[1]
        : ""

      this.s.selectedUploadedFile = {
        contentBase64,
        contentType: file.type || "application/octet-stream",
        filename: file.name
      }
    }
    reader.onerror = () => {
      this.s.status = {
        kind: "error",
        text: errorMessage(reader.error || new Error("Could not read the selected file"))
      }
    }
    reader.readAsDataURL(file)
  }
}

/** @type {NamedExoticComponent<UploadedFilesEditScreenProps>} */
const UploadedFilesEditScreenComponent = memo(shapeComponent(UploadedFilesEditScreen))

export default UploadedFilesEditScreenComponent

/**
 * Normalizes an optional query value into a number.
 * @param {string | null} value Query value from route params.
 * @returns {string | null} Identifier value or null when absent.
 */
function normalizeQueryNumber(value) {
  if (typeof value !== "string" || !value.trim()) return null

  return value.trim()
}

/**
 * Normalizes an optional query value into a string.
 * @param {string | null} value Query value from route params.
 * @returns {string | null} Trimmed string or null when absent.
 */
function normalizeQueryString(value) {
  if (typeof value !== "string") return null

  const normalized = value.trim()

  return normalized || null
}
